import { ReactComponent as CattleMap } from "../../../../assets/svg/icons/cattle.svg";
import { useFilter } from "../../../../hooks/useFilter";
import Constants from "../../../../constants/Index";
import axios from "../../../../services/axios";
import { MultiDropdownV2 } from "../../../../components/FormControlsV2";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import "../styles.css";
import { IBatch } from "../../../../@types/API/IBatches";
import { IAreas } from "../../../../@types/API/IArea";
import { IOptionProps } from "../../../../@types/reactSelect/IOption";
import { ButtonExportPDF } from "../../../../components/Buttons/ButtonExportPDF";
import { ButtonExportExcel } from "../../../../components/Buttons/ButtonExportExcel";
import { exportBatchesDataToExcel } from "../../../../utils/excel/exportBatchesData";
import { sortBy } from "../../../../utils/arrays/sortBy";
import DownloadChip from "../../../../components/Chips/ComponentChipDownload/Index";
import {
  selectionToNumericIds,
  selectionToSortedIdsKey,
  useFilterContextV2,
} from "../../../../contexts/FilterContextV2";


export default function DashboardCattleMapBatches() {
  const {
    setHideMicroAreaFilter,
    setHideBatchFilter,
    setDashboard,
    setSideBarOptionSelected,
    batchCategory,
    setBatchCategory
  } = useFilter();

  const { selectedFarms, selectedAreas } = useFilterContextV2();

  const farmsKey = useMemo(
    () => selectionToSortedIdsKey(selectedFarms),
    [selectedFarms],
  );
  const areasKey = useMemo(
    () => selectionToSortedIdsKey(selectedAreas),
    [selectedAreas],
  );

  const {
    control,
    watch
  } = useForm();

  const [areas, setAreas] = useState<IAreas>();
  const [batches, setBatches] = useState<IBatch[]>();
  const batchesSelected = watch("batches")

  useEffect(() => {
    setHideMicroAreaFilter(true)
    setHideBatchFilter(true)
    setSideBarOptionSelected("DASHBOARD")
    setDashboard("BATCHES")
    document.title = Constants.TITLE_DASHBOARD;
  }, []);

  const farmIds = useMemo(
    () => selectionToNumericIds(selectedFarms),
    [selectedFarms],
  );
  const selectedAreaIds = useMemo(
    () => selectionToNumericIds(selectedAreas),
    [selectedAreas],
  );

  useEffect(() => {
    if (!farmIds.length) {
      setAreas(undefined);
      setBatches(undefined);
      return;
    }
    let cancelled = false;
    axios.get(`/areas?farm_id=${farmIds[0]}`)
      .then((responses) => {
        if (cancelled) return;
        setAreas(responses.data as IAreas);
      })
      .catch(() => {
        if (!cancelled) setAreas(undefined);
      });
    axios.get(`/batches?farm_id=${farmIds[0]}`)
      .then((response) => {
        if (cancelled) return;
        setBatches(response.data);
      })
      .catch(() => {
        if (!cancelled) setBatches(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [farmsKey]);

  useEffect(() => {
    if (!farmIds.length || !areas?.length) return;

    const farmId = farmIds[0];

    const defaultAreas = areas.map((area) => area.id) as (string | number)[];

    const areasIds =
      selectedAreaIds.length > 0 ? selectedAreaIds : defaultAreas.map(Number);

    const idsForFarm = areasIds.filter((aid) => {
      const ar = areas.find((a) => String(a.id) === String(aid));
      return ar && Number((ar as any).farm?.id) === farmId;
    });
    const paramAreas =
      idsForFarm.length > 0
        ? idsForFarm
        : areas
          .filter((a) => Number((a as any).farm?.id) === farmId)
          .map((a) => a.id);

    axios
      .get(
        `/batches/categories?farmId=${farmId}&areaIds=${paramAreas}&${batchesSelected?.length > 0 ? batchesSelected.map((batch: IOptionProps) => `batchIds=${batch.value}`).join("&") : ""}&size=${999}`,
      )
      .then((r) => r.data)
      .then((data) => {
        const content = [...(data?.content || [])];
        if (content.length) {
          content.sort((a: any, b: any) => {
            const keyA = `${a.farm_initials}-${a.area_initials}-${a.initials}`;
            const keyB = `${b.farm_initials}-${b.area_initials}-${b.initials}`;
            return keyA.localeCompare(keyB, undefined, { sensitivity: "base" });
          });
        }
        setBatchCategory(
          data ? { ...data, content } : { content },
        );
      });
  }, [farmsKey, areasKey, areas, batchesSelected]);

  return (
    <div className="cattleMap_background cattleMap_batchesPrint" style={{ height: "100vh", overflowY: "auto" }}>
      <form className="dashboardPastureManagement">

        <div className="cattleMap_page">
          <div className="cattleMap_header">
            <CattleMap height={72} />
            <div style={{
              display: "flex",
              flexDirection: "column",
            }}>
              <h2 className="cattleMap_title">
                Mapa de Gado
              </h2>
              <h3 className="cattleMap_subtitle">
                Visão de Lotes
              </h3>
            </div>
          </div>

          <div style={{ width: "100%", maxWidth: 400, display: "flex", flexDirection: "column", gap: 16, marginBottom: 24 }}>
            <Controller
              name="batches"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <MultiDropdownV2
                  name="batches"
                  label="Lotes"
                  placeholder="Selecione..."
                  menuMount="overlay"
                  emptyMessage="Nenhuma opção disponível"
                  options={
                    batches
                      ?.filter((batch) => batch.status === "ACTIVE")
                      .map((batch) => ({
                        value: batch.id,
                        label: `${batch.farm.initials}-${batch.area.initials}-${batch.initials}`,
                      }))
                      .sort(sortBy("label")) || []
                  }
                  value={field.value ?? []}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={error?.message}
                />
              )}
            />

            <DownloadChip
              enableExcel
              enablePdf
              onExcelSelect={() => {
                try {
                  exportBatchesDataToExcel(batchCategory);
                } catch (error) {
                  console.error('Erro ao exportar:', error);
                  alert('Erro ao exportar dados. Tente novamente.');
                }
              }}
              onPdfSelect={() => {
                window.print()
              }}
            />
          </div>

          <div className="tableDashboard">
            <table>
              <thead style={{
                color: "#fff"
              }}>
                <tr>
                  <th scope="col" rowSpan={2} style={{
                    background: "#324440",
                    width: "150px",
                    textAlign: "left"
                  }}>
                    Lotes
                  </th>
                  <th scope="col" rowSpan={2} style={{
                    background: "#324440",
                    width: "150px",
                    textAlign: "left"
                  }}>
                    Pasto
                  </th>
                  <th scope="col" colSpan={6} style={{
                    textAlign: "center",
                    background: "#62b8ae"
                  }}>
                    Macho
                  </th>
                  <th scope="col" colSpan={5} style={{
                    textAlign: "center",
                    background: "#ff6161"
                  }}>
                    Fêmea
                  </th>
                  <th scope="col" rowSpan={2} style={{
                    background: "#57CE71",
                    width: "80px"
                  }}>
                    Saldo
                  </th>
                </tr>
                <tr>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    0 a 8 meses
                  </th>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    9 a 12 meses
                  </th>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    13 a 24 meses
                  </th>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    25 a 36 meses
                  </th>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    {`> 37 meses`}
                  </th>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    Touros
                  </th>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    0 a 8 meses
                  </th>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    9 a 12 meses
                  </th>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    13 a 24 meses
                  </th>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    25 a 36 meses
                  </th>
                  <th scope="col" style={{
                    background: "#324440",
                    width: "80px"
                  }}>
                    {`> 37 meses`}
                  </th>
                </tr>
              </thead>

              <tbody>
                {batchCategory && batchCategory.content.map((batch, index) => (
                  <tr
                    key={`${batch.farm_initials}-${batch.area_initials}-${batch.initials}-${index}`}
                    className={index % 2 === 0 ? "bg_white" : "bg_gray"}
                  >
                    <th scope="row">{`${batch.farm_initials}-${batch.area_initials}-${batch.initials}`}</th>
                    <td>
                      {`${batch.farm_initials}-${batch.area_initials}-${batch.micro_area_initials}`}
                    </td>
                    {[6, 7, 8, 9, 10, 11].map((id) => (
                      <td key={`male-${id}`}>
                        {batch.categories.find((category) => category.id === id)?.quantity || 0}
                      </td>
                    ))}
                    {[1, 2, 3, 4, 5].map((id) => (
                      <td key={`female-${id}`}>
                        {batch.categories.find((category) => category.id === id)?.quantity || 0}
                      </td>
                    ))}
                    <td>
                      {batch.categories.reduce((sum, category) => sum + category.quantity, 0)}
                    </td>
                  </tr>
                ))}
              </tbody>

              <tfoot
                style={{
                  background: "#324440",
                  color: "#fff"
                }}
              >
                <tr>
                  <th style={{
                    textAlign: "left"
                  }}>Total</th>
                  <td/>
                  {[6, 7, 8, 9, 10, 11].map((id) => (
                    <td key={`male-${id}`}>
                      {batchCategory
                        ? batchCategory.content.reduce(
                          (sum, batch) =>
                            sum +
                            (batch.categories.find((category) => category.id === id)?.quantity || 0),
                          0
                        )
                        : 0}
                    </td>
                  ))}
                  {[1, 2, 3, 4, 5].map((id) => (
                    <td key={`female-${id}`}>
                      {batchCategory
                        ? batchCategory.content.reduce(
                          (sum, batch) =>
                            sum +
                            (batch.categories.find((category) => category.id === id)?.quantity || 0),
                          0
                        )
                        : 0}
                    </td>
                  ))}
                  <td>
                    {batchCategory
                      ? batchCategory.content.reduce(
                        (sum, batch) =>
                          sum +
                          batch.categories.reduce((batchSum, category) => batchSum + category.quantity, 0),
                        0
                      )
                      : 0}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </form>
    </div>
  );
}
